import React from "react";
import { ArrowRight, Star, Linkedin, Instagram } from "lucide-react";
import { track } from "../utils/analytics";

// Hero section with CTA and social links
const Hero: React.FC = () => {
  return (
    <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-8 items-center">
          {/* Left Content */}
          <div className="space-y-8 max-w-2xl mx-auto flex flex-col items-center text-center">
            <div className="space-y-6">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-normal leading-tight tracking-tight">
                <span className="text-gray-900">Bear Strategy Lab</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                The premier student-run, software and strategy consulting firm at Berkeley.
              </p>
            </div>

            {/* CTA, Rating, and Icons */}
            <div className="flex flex-col items-center gap-4 w-full max-w-xl">
              {/* CTA and Rating */}
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full justify-center">
                <a
                  href="https://calendly.com/rohansontakke/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary group"
                  onClick={() =>
                    track("book_call", {
                      page: window.location.pathname,
                      location: "hero",
                    })
                  }
                >
                  Book a call
                  <ArrowRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </a>

                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    <img
                      src="https://i.pravatar.cc/150?img=12"
                      alt="Customer 1"
                      className="w-8 h-8 rounded-full border-2 border-white object-cover"
                    />
                    <img
                      src="https://i.pravatar.cc/150?img=33"
                      alt="Customer 2"
                      className="w-8 h-8 rounded-full border-2 border-white object-cover"
                    />
                    <img
                      src="https://i.pravatar.cc/150?img=47"
                      alt="Customer 3"
                      className="w-8 h-8 rounded-full border-2 border-white object-cover"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={14}
                          className="fill-yellow-400 text-yellow-400"
                        />
                      ))}
                    </div>
                    <p className="text-sm text-gray-600">
                      4.9/5 Customer Score
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Icons */}
              <div className="flex gap-2 justify-center w-full mt-2">
                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/company/bear-strategy-lab/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
                  title="LinkedIn"
                  onClick={() =>
                    track("linkedin_click", {
                      page: window.location.pathname,
                    })
                  }
                >
                  <Linkedin size={14} />
                </a>
                {/* Instagram */}
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
                  title="Instagram"
                >
                  <Instagram size={14} />
                </a>
                {/* X (Twitter) */}
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
                  title="X (Twitter)"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative lg:col-span-1">
            {/* Main visual container with video background */}
            <div className="relative h-96 lg:h-[480px] w-full rounded-3xl overflow-hidden lg:scale-95 lg:-translate-x-2">
              {/* Abstract Gradient Video Background */}
              <video
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
              >
                <source
                  src="/Abstract_Gradient_Video_Generation.mp4"
                  type="video/mp4"
                />
              </video>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
